import { useRouter } from "next/router";
import { usePlausible } from "next-plausible";
import { useEffect, useRef, useState } from "react";

import { FACEBOOK_PIXEL_ID, SNAPCHAT_PIXEL_ID } from "@/constants";
import { useProfile } from "@/hooks/profile";
import useGlobalState from "@/hooks/useGlobalState";
import { authStatusFromProfileQuery } from "@/hooks/useProfile";
import { GTM } from "@/lib/gtm";
import { formatPriceWithoutCurrencyString } from "@/utils/format";
import { isVercelProduction } from "@/utils/helpers";

const useAnalytics = () => {
  const router = useRouter();
  const profile = useProfile();
  const { isLoggedIn } = authStatusFromProfileQuery(profile);
  const { hasAcceptedCookies, acceptCookies } = useGlobalState();

  const [pixelLibrariesLoaded, setPixelLibrariesLoaded] = useState(false);
  const fbPixel = useRef(null);
  const scPixel = useRef(null);

  const initializePixels = async () => {
    if (pixelLibrariesLoaded || !hasAcceptedCookies || !isVercelProduction)
      return;

    const [FacebookPixelModule, SnapchatPixelModule] = await Promise.all([
      import("react-facebook-pixel"),
      import("next-snapchat-pixel"),
    ]);

    fbPixel.current = FacebookPixelModule.default;
    scPixel.current = SnapchatPixelModule.default;

    fbPixel.current.init(FACEBOOK_PIXEL_ID);
    scPixel.current.init(SNAPCHAT_PIXEL_ID);

    setPixelLibrariesLoaded(true);
    trackPageViewsOnRouteChange();
  };

  const trackFacebookEvent = async (
    eventName: string,
    data: Record<string, unknown>
  ) => {
    if (!fbPixel.current) await initializePixels();
    fbPixel.current?.track(eventName, data);
  };

  const trackSnapchatEvent = async (
    eventName: string,
    data: Record<string, unknown>
  ) => {
    if (!scPixel.current) await initializePixels();
    scPixel.current?.trackWithData(eventName, data);
  };

  const plausible = usePlausible();

  const trackPlausibleEvent = async (
    eventName: string,
    data: Record<string, unknown>
  ) => {
    plausible(eventName, data);
  };

  const trackAddToCartEvent = async (price, currency, id) => {
    const formattedPrice = formatPriceWithoutCurrencyString(price);
    await trackFacebookEvent("AddToCart", {
      value: formattedPrice,
      currency,
    });
    await trackSnapchatEvent("ADD_CART", {
      value: formattedPrice,
      currency,
    });
    await trackPlausibleEvent("addToCart", {
      revenue: {
        currency,
        amount: formattedPrice,
      },
    });
    GTM.addToCart(Number(formattedPrice), currency, id);
  };

  const trackPageViewsOnRouteChange = () => {
    const handleRouteChange = () => {
      fbPixel.current.pageView();
      scPixel.current.track("PAGE_VIEW");
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  };

  useEffect(() => {
    if (isLoggedIn) acceptCookies();
  }, [isLoggedIn]);

  return {
    initializePixels,
    trackFacebookEvent,
    trackSnapchatEvent,
    trackPlausibleEvent,
    trackAddToCartEvent,
  };
};

export default useAnalytics;
